/**
 * Module: screens/creator/steps/PromoScreen
 * Role: Wizard step 0 — «Стать креатором» pitch screen (FTR-1131 amendment
 *       2026-06-07, design Eqq6AbBPpm1zPPyErlkgJQ chat16). Promo cards on
 *       Home/Tasks/Profile route here (not directly to /url). Header carries
 *       a back-pill only (no title, no progress strip — design L132-137):
 *       founder report 2026-06-07 «Кнопка "Стать креатором" ведёт сразу на
 *       верификацию, а не на экран стать креатором».
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/_chrome/WizardShell:WizardFrame, WizardHeader,
 *     WizardBody, WizardCtaBar
 *   - components/atoms/{Card,Ic}
 *   - styles/tokens:P, T
 *
 * Used by: CreatorOnboardingScreen.tsx
 * ADR-022 §7: no openExternal(BOT_LINK) — Back closes wizard, goes to /profile.
 * ADR-023: no window.Telegram / platform literals outside src/platform/
 */

import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/atoms/Card';
import { Ic, type IconName } from '../../../components/atoms/Ic';
import { P, T } from '../../../styles/tokens';
import { WizardBody, WizardCtaBar, WizardFrame, WizardHeader } from '../_chrome/WizardShell';

interface PromoFeature {
  icon: IconName;
  title: string;
  subtitle: string;
}

// Copy from `creator-onboarding.jsx:152-156`. Keep wording 1:1 — these are
// founder-approved marketing lines (chat16 «комить»).
const FEATURES: PromoFeature[] = [
  {
    icon: 'tag',
    title: 'Доступ к закрытым кампаниям',
    subtitle: 'Pantene, бренды P&G и другие',
  },
  {
    icon: 'card',
    title: 'Кешбэк выше обычного',
    subtitle: 'до 100% от стоимости товара',
  },
  {
    icon: 'chart',
    title: 'Профиль для подбора',
    subtitle: 'бренды находят вас по аудитории',
  },
];

export default function PromoScreen() {
  const navigate = useNavigate();

  return (
    <WizardFrame>
      <WizardHeader onBack={() => navigate('/profile')} />
      <WizardBody style={{ padding: '12px 20px 8px', gap: 0 }}>
        {/* Hero flame badge — design L139-145 */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: P.terracotta,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 24px rgba(192,81,58,0.28)',
            marginBottom: 20,
          }}
        >
          <Ic name="flame" size={34} color="#ffffff" />
        </div>

        <div style={{ font: T.title1, color: P.ink, marginBottom: 8 }}>
          Стать креатором
        </div>
        <div style={{ font: T.callout, color: P.inkSoft, lineHeight: 1.5, marginBottom: 22 }}>
          Премиум-задания с высоким кешбэком и бартером от брендов. Нужно один
          раз подтвердить аккаунт и заполнить профиль — это займёт 3–5 минут.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FEATURES.map((f) => (
            <Card
              key={f.title}
              p={14}
              style={{ display: 'flex', gap: 12, alignItems: 'center' }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: P.terraSoft,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Ic name={f.icon} size={19} color={P.terraDark} />
              </div>
              <div>
                <div style={{ font: T.subhead, fontWeight: 600, color: P.ink }}>
                  {f.title}
                </div>
                <div style={{ font: T.caption, color: P.inkSoft }}>{f.subtitle}</div>
              </div>
            </Card>
          ))}
        </div>
      </WizardBody>
      <WizardCtaBar
        label="Начать верификацию"
        onClick={() => navigate('/creator-onboarding/url')}
      />
    </WizardFrame>
  );
}
